import { ARENA_ART, type Vec2, type WeaponTier, type WeaponType } from '@chessshooter/shared';

// Presentation and control constants of the client. Simulation tuning lives in shared/src/constants.ts.
// The touch scheme follows surviv.io: left half of the screen moves, right half aims, see docs/STEUERUNG.md.

export { PAD_RADIUS, PAD_PORTRAIT_SCALE } from '@chessshooter/shared';
/** CSS px of deflection below which a pad counts as idle (thumb jitter) */
export const PAD_DEAD_ZONE = 6;
/** fraction of the pad radius beyond which the aim pad counts as aiming; releasing inside cancels */
export const AIM_START = 0.4;
/** a press on the aim half released within this time without ever aiming is a quick shot straight ahead */
export const QUICK_TAP_MS = 200;
/** locked pad centres, measured from the bottom corners (surviv.io values) */
export const LOCKED_PAD_LANDSCAPE: Vec2 = { x: 126, y: 100 };
export const LOCKED_PAD_PORTRAIT: Vec2 = { x: 96, y: 160 };
/** moving without aiming for this long turns the facing direction into the move direction */
export const TURN_COOLDOWN_MS = 500;
/** how far ahead of the player a shot from a direction is aimed, world units (beyond the arena) */
export const AIM_REACH = 20 * 32;
/**
 * shot preview of the held weapon along the assisted aim: dots along every pellet's path (radius at
 * least dotRadius, else dotScale times the projectile), a band as wide as a beam, a dashed ring where
 * a rocket would burst
 */
export const AIM_LINE = { startGap: 20, spacing: 10, dotRadius: 1.6, dotScale: 0.5, alpha: 0.45, beamAlpha: 0.3, ringAlpha: 0.7, ringDash: [4, 4] } as const;
/** largest catch-up per frame; beyond this the simulation drops time instead of spiralling */
export const MAX_FRAME_MS = 250;

export const COLORS = {
  background: '#273d3b',
  floor: '#293c37',
  floorAlt: '#263932',
  paving: '#35473f',
  pavingAlt: '#32443c',
  grid: 'rgba(12,25,21,0.18)',
  wall: '#697e78',
  wallEdge: '#a3b2a1',
  wallDark: '#3d5652',
  stone: '#b4c0ad',
  stoneLight: '#d7ddc7',
  stoneDark: '#7d9587',
  shadow: 'rgba(28,51,42,0.22)',
  crate: '#c2945d',
  crateLight: '#edc487',
  crateEdge: '#77583d',
  ...ARENA_ART.teamColors,
  outline: '#293e3b',
  visor: '#203b40',
  visorLight: '#c5efe2',
  pickup: '#263e3a',
  white: '#fff7dc',
  padBase: 'rgba(22,44,41,0.30)',
  padBaseLocked: 'rgba(22,44,41,0.18)',
  padRing: 'rgba(239,255,231,0.65)',
  padKnob: 'rgba(239,255,231,0.65)',
  padKnobAiming: 'rgba(255,207,114,0.85)',
  facing: 'rgba(255,255,255,0.7)',
} as const;

/** ring around a pickup: its weapon's tier at a glance */
export const TIER_RING: Record<WeaponTier, string> = { ...ARENA_ART.tierColors, 1: 'rgba(255,255,255,0.45)' };

/** Pickup names, compact HUD labels and weapon effect colours. */
export const WEAPON_STYLE: Record<WeaponType, { letter: string; color: string; name: string }> = {
  pistol: { name: 'Pistole', letter: 'P', color: '#e8e8e8' },
  shotgun: { name: 'Schrot', letter: 'S', color: '#f0c040' },
  burst: { name: 'Salve', letter: 'B', color: '#9be564' },
  curve: { name: 'Kurve', letter: '~', color: '#64e0c8' },
  hexa: { name: 'Hexa', letter: '6', color: '#c8a2ff' },
  flame: { name: 'Flamme', letter: 'F', color: '#ff9f1c' },
  laser: { name: 'Laser', letter: 'L', color: '#5cf0ff' },
  cannon: { name: 'Kanone', letter: 'C', color: '#ff7a45' },
  rocket: { name: 'Rakete', letter: 'R', color: '#e05cff' },
};

/** beams: the core line plus a wider, fainter glow; flames flicker in width */
export const BEAM_STYLE = { glowFactor: 2.2, glowAlpha: 0.25, flickerFactor: 0.25 } as const;
/** blasts grow from this fraction of their radius to the full radius while fading */
export const BLAST_STYLE = { startFraction: 0.5, fillAlpha: 0.45, ringAlpha: 0.9 } as const;

/** the board in the chess phase: CSS px, or fractions of a square where noted */
export const BOARD_STYLE = {
  /** gap between the board and the screen edges */
  margin: 8,
  /** strip kept free above the board for the status line ("Du bist am Zug") */
  statusHeight: 30,
  frame: 3,
  coordFont: '600 10px system-ui, sans-serif',
  coordInset: 2,
  /** dot on an empty target square (fraction of a square) */
  targetDot: 0.16,
  /** ring around a piece that can be taken: radius and line width (fractions) */
  captureRing: 0.45,
  captureRingWidth: 0.09,
  /** the dragged piece grows a little so the finger does not hide it */
  dragScale: 1.2,
  /** the piece left behind on the origin square while dragging */
  ghostAlpha: 0.35,
  /** the check marker: a radial glow under the king, inner and outer radius (fractions) */
  checkCore: 0.1,
  checkGlow: 0.75,
  /** the stand-in until a piece's SVG has loaded: disc radius and letter size (fractions) */
  fallbackDisc: 0.36,
  fallbackFont: 0.4,
} as const;

export const BOARD_COLORS = {
  background: COLORS.background,
  backgroundWhite: '#e5eddd',
  backgroundBlack: '#344e3c',
  light: '#e8d9b5',
  dark: '#a8835e',
  frame: '#293e3b',
  lastMove: 'rgba(240,192,64,0.5)',
  premove: 'rgba(141,94,230,0.7)',
  selected: 'rgba(57,201,180,0.65)',
  target: 'rgba(41,62,59,0.3)',
  hover: 'rgba(255,247,220,0.9)',
  check: 'rgba(229,72,77,0.85)',
  checkFade: 'rgba(229,72,77,0)',
  coordOnLight: '#a8835e',
  coordOnDark: '#e8d9b5',
  fallbackWhite: '#fff7dc',
  fallbackBlack: '#293e3b',
} as const;

/** a clock below this is drawn as running out */
export const LOW_CLOCK_MS = 30_000;

/** Time-loss badges stay legible at every arena zoom; dimensions are CSS pixels. */
export const TIME_LOSS_STYLE = {
  durationMs: 2200, fadeStart: 0.75, rise: 22, gap: 12,
  font: '900 24px system-ui, sans-serif', height: 36, padding: 12,
  corner: 8, edge: 2, margin: 6,
  background: '#8c2430', border: '#ffe0a3', text: '#fff7dc',
} as const;

export const WEAPON_FINISH = {
  metal: '#9bb3b7', light: '#e6f1e5', dark: '#344d55', grip: '#715642',
  edge: '#14292f', outline: 1.5, corner: 1.2,
} as const;

type GunRect = readonly [number, number, number, number];
interface WeaponArt {
  hull: readonly (readonly [number, number])[];
  barrels: readonly GunRect[];
  grips: readonly GunRect[];
  accents: readonly GunRect[];
  rails: readonly GunRect[];
  core?: readonly [number, number, number];
}

/** Local drawing coordinates; silhouettes stay legible at pickup size. */
export const WEAPON_ART: Record<WeaponType, WeaponArt> = {
  pistol: { hull: [[-10,-4],[8,-4],[10,-2],[10,3],[-9,3]], barrels: [[8,-2,7,4]], grips: [[-7,2,6,8]], accents: [[-7,-3,9,2]], rails: [[3,-5,3,2]] },
  shotgun: { hull: [[-12,-5],[3,-5],[7,-3],[7,5],[-12,5]], barrels: [[3,-4,15,3],[3,1,15,3]], grips: [[-17,-3,6,7],[-7,3,5,7]], accents: [[-3,-5,7,10]], rails: [[-1,-4,1,8],[2,-4,1,8],[15,-5,3,10]] },
  burst: { hull: [[-12,-5],[5,-5],[9,-2],[9,4],[-9,4]], barrels: [[7,-2,12,4]], grips: [[-17,-3,6,7],[-6,3,5,8],[1,3,5,6]], accents: [[-8,-4,12,3]], rails: [[-4,-7,8,2],[12,-3,2,6],[16,-3,2,6]] },
  curve: { hull: [[-12,-2],[-6,-7],[5,-7],[13,-3],[7,-1],[0,-3],[-3,0],[0,3],[7,1],[13,3],[5,7],[-6,7]], barrels: [], grips: [[-10,2,5,8]], accents: [[-2,-7,6,2],[-2,5,6,2]], rails: [], core: [-6,0,3] },
  hexa: { hull: [[-10,-6],[0,-11],[10,-6],[10,6],[0,11],[-10,6]], barrels: [[9,-2,7,4],[-16,-2,7,4],[-6,-13,4,5],[2,-13,4,5],[-6,8,4,5],[2,8,4,5]], grips: [], accents: [[-7,-5,14,10]], rails: [], core: [0,0,5] },
  cannon: { hull: [[-13,-5],[-7,-8],[7,-8],[11,-5],[11,5],[7,8],[-7,8],[-13,5]], barrels: [[8,-5,11,10]], grips: [[-10,4,6,8]], accents: [[-5,-7,7,14]], rails: [[14,-7,5,14]], core: [-8,0,2] },
  flame: { hull: [[-12,-5],[4,-5],[9,-3],[9,4],[-12,4]], barrels: [[6,-3,12,6]], grips: [[-8,3,5,8],[-9,5,13,5]], accents: [[-9,-4,10,3],[-7,6,9,3]], rails: [[11,-5,3,10],[16,-5,3,10]], core: [18,0,2] },
  laser: { hull: [[-14,-4],[4,-4],[10,-7],[18,-7],[18,-4],[8,0],[18,4],[18,7],[10,7],[4,4],[-14,4]], barrels: [[-1,-2,16,4]], grips: [[-10,3,5,7]], accents: [[-10,-3,3,6],[-5,-3,3,6],[10,-7,7,2],[10,5,7,2]], rails: [[-10,-6,8,2]], core: [4,0,3] },
  rocket: { hull: [[-15,-6],[10,-6],[17,-3],[17,3],[10,6],[-15,6]], barrels: [[-18,-7,4,14],[13,-4,7,8]], grips: [[-7,5,5,7],[4,5,4,5]], accents: [[-8,-5,4,10],[5,-5,4,10]], rails: [[-3,-8,7,2]], core: [16,0,3] },
};

export const SHOT_STYLE = { ticks: 8, reach: 18, spread: 6, halo: 2.4, haloAlpha: 0.16, coreWidth: 0.32, sparks: 9, sparkLength: 10 } as const;

export const ARENA_AUDIO = { volume: 0.42, maxVoices: 12, maxGapTicks: 30, panWidth: 400, distance: 650, minimumGain: 0.25, attack: 0.003, release: 0.025 } as const;
export interface SoundPatch { duration: number; startHz: number; endHz: number; tone: number; noise: number; decay: number; grit: number }
export const ARENA_SOUND_PATCHES: Record<WeaponType | 'explosion' | 'pickup' | 'hit', SoundPatch> = {
  pistol: { duration: 0.16, startHz: 230, endHz: 65, tone: 0.45, noise: 0.65, decay: 7, grit: 0.65 },
  shotgun: { duration: 0.3, startHz: 135, endHz: 38, tone: 0.55, noise: 0.85, decay: 6, grit: 0.3 },
  burst: { duration: 0.12, startHz: 310, endHz: 90, tone: 0.35, noise: 0.65, decay: 8, grit: 0.8 },
  curve: { duration: 0.3, startHz: 380, endHz: 1050, tone: 0.65, noise: 0.12, decay: 4, grit: 0.15 },
  hexa: { duration: 0.34, startHz: 620, endHz: 110, tone: 0.65, noise: 0.25, decay: 5, grit: 0.3 },
  cannon: { duration: 0.46, startHz: 95, endHz: 28, tone: 0.8, noise: 0.55, decay: 6, grit: 0.15 },
  flame: { duration: 1.05, startHz: 75, endHz: 40, tone: 0.2, noise: 0.8, decay: 2, grit: 0.35 },
  laser: { duration: 0.32, startHz: 1800, endHz: 130, tone: 0.65, noise: 0.08, decay: 5, grit: 0.1 },
  rocket: { duration: 0.5, startHz: 180, endHz: 45, tone: 0.45, noise: 0.7, decay: 3, grit: 0.2 },
  explosion: { duration: 0.65, startHz: 80, endHz: 24, tone: 0.75, noise: 0.9, decay: 5, grit: 0.12 },
  pickup: { duration: 0.19, startHz: 660, endHz: 1320, tone: 0.55, noise: 0, decay: 4, grit: 0 },
  hit: { duration: 0.12, startHz: 420, endHz: 180, tone: 0.45, noise: 0.4, decay: 7, grit: 0.6 },
};
